import {
  Activity,
  AlertTriangle,
  BarChart3,
  Briefcase,
  Building2,
  Calculator,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  Gauge,
  HardHat,
  Home,
  Inbox,
  Layers,
  LogOut,
  Mail,
  Menu,
  ScrollText,
  Settings,
  Tags,
  Users,
  Webhook,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useCurrentUser, useInbox, useLogout } from "@/api/hooks";
import { CONTRACTOR_APP_ORIGIN, IS_STAFF_CONSOLE } from "@/appTarget";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { TermsReacceptModal } from "@/components/TermsReacceptModal";

const COLLAPSED_KEY = "pencilout_sidebar_collapsed";

// ---------------------------------------------------------------
// Nav config — items grouped into sections so the sidebar reads
// as "what you do" rather than a flat 11-item list. Sections
// render in order; items within a section render in order.
// ---------------------------------------------------------------

interface NavItem {
  to: string;
  label: string;
  icon: typeof Briefcase;
  /** Targets the link should appear on. */
  targets: ReadonlyArray<"app" | "admin">;
  /** Match the route exactly (no prefix matching). Default false. */
  end?: boolean;
}

interface NavSection {
  /** Section header. Empty string ⇒ no header rendered (used for
   *  the top "primary" group that doesn't need a label). */
  title: string;
  items: NavItem[];
  targets: ReadonlyArray<"app" | "admin">;
}

const NAV_SECTIONS: NavSection[] = [
  // Contractor app — primary daily-use surfaces. Order follows the
  // contractor's mental funnel: pulse (Dashboard) → who (Clients) →
  // what work (Jobs) → what's quoted (Quotes) → who's helping (Subs).
  // Inbox is a comm tab, distinct from the funnel items, so it
  // tails the section.
  {
    title: "Workspace",
    targets: ["app"],
    items: [
      { to: "/", label: "Dashboard", icon: Gauge, targets: ["app"], end: true },
      { to: "/clients", label: "Clients", icon: Home, targets: ["app"] },
      { to: "/jobs", label: "Jobs", icon: Briefcase, targets: ["app"], end: true },
      { to: "/quotes", label: "Quotes", icon: FileText, targets: ["app"] },
      { to: "/subcontractors", label: "Subs", icon: HardHat, targets: ["app"] },
      { to: "/inbox", label: "Inbox", icon: Inbox, targets: ["app"] },
    ],
  },
  // Contractor — config that's set up once + tweaked rarely.
  {
    title: "Setup",
    targets: ["app"],
    items: [
      { to: "/pricing-rules", label: "Pricing rules", icon: Tags, targets: ["app"] },
      { to: "/job-templates", label: "Job templates", icon: Layers, targets: ["app"] },
      { to: "/webhooks", label: "Webhooks", icon: Webhook, targets: ["app"] },
    ],
  },
  // Contractor — billing + branding/business.
  {
    title: "Account",
    targets: ["app"],
    items: [
      { to: "/billing", label: "Plan & billing", icon: CreditCard, targets: ["app"] },
      { to: "/settings", label: "Settings", icon: Settings, targets: ["app"] },
    ],
  },
  // Staff — primary review surfaces (live customer state).
  {
    title: "Customers",
    targets: ["admin"],
    items: [
      {
        to: "/organizations",
        label: "Organizations",
        icon: Building2,
        targets: ["admin"],
      },
      { to: "/users", label: "Users", icon: Users, targets: ["admin"] },
      // Staff version of pricing rules — same route as contractor,
      // but the page surfaces Add-missing / Reset defaults buttons
      // when the caller is pencilout_admin. Staff picks an org via
      // the in-page filter dropdown to target one.
      {
        to: "/pricing-rules",
        label: "Pricing rules",
        icon: Tags,
        targets: ["admin"],
      },
    ],
  },
  // Staff — analytics + post-hoc inspection.
  {
    title: "Analytics",
    targets: ["admin"],
    items: [
      { to: "/usage", label: "AI usage", icon: BarChart3, targets: ["admin"] },
      {
        to: "/pricing-calculator",
        label: "Calculator",
        icon: Calculator,
        targets: ["admin"],
      },
      {
        to: "/failed-walkthroughs",
        label: "Failed walkthroughs",
        icon: AlertTriangle,
        targets: ["admin"],
      },
    ],
  },
  // Staff — audit / event streams.
  {
    title: "Audit",
    targets: ["admin"],
    items: [
      { to: "/audit", label: "Audit log", icon: ScrollText, targets: ["admin"] },
      {
        to: "/billing-events",
        label: "Stripe events",
        icon: CreditCard,
        targets: ["admin"],
      },
      { to: "/email-events", label: "Email events", icon: Mail, targets: ["admin"] },
      { to: "/telemetry", label: "Telemetry", icon: Activity, targets: ["admin"] },
    ],
  },
  // Staff — platform config.
  {
    title: "Platform",
    targets: ["admin"],
    items: [{ to: "/settings", label: "Settings", icon: Settings, targets: ["admin"] }],
  },
];

/** App shell — sidebar nav on desktop, hamburger drawer on mobile.
 *
 *  Replaces the prior horizontal top-bar nav, which couldn't fit
 *  the 11-item contractor menu without wrapping or scrolling.
 *  Sidebar gives every item a visible row, scales to 20+ items
 *  vertically, and uses the wide-screen real estate that the old
 *  layout left as empty whitespace flanking the centered content.
 *
 *  Collapsible — the chevron at the bottom toggles between full-
 *  label (240px) and icon-only (64px). Persisted in localStorage
 *  so the user's preference survives reloads.
 *
 *  Mobile: sidebar hides off-screen below ``lg`` (1024px). The
 *  hamburger in the slim top bar opens it as a drawer overlay
 *  with a backdrop that closes on tap.
 *
 *  Build-target gating (contractor vs. staff console) still
 *  happens via ``targets`` on each section + item — same as the
 *  old NAV array, just nested one level deeper.
 */
export function Layout() {
  const me = useCurrentUser();
  const logout = useLogout();
  const inbox = useInbox();
  const inboxCount = inbox.data?.length ?? 0;
  const targetForFilter: "app" | "admin" = IS_STAFF_CONSOLE ? "admin" : "app";

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const setCollapsedPersisted = (next: boolean) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, next ? "true" : "false");
    } catch {
      /* localStorage unavailable; in-memory only */
    }
  };

  // Cross-host bounce when a non-staff session lands on the staff
  // console — almost always the impersonation case (staff hits
  // "Sign in as" → cookie now reflects the impersonated org user
  // but the host is still admin.*). The staff routes all gate on
  // pencilout_admin and the contractor routes aren't even mounted
  // on this build, so leaving the user here means every link is
  // a dead end. Hop them over to the contractor SPA. Bearer cookie
  // is on .pencilout.app so the impersonation session carries
  // across subdomains untouched, and the existing
  // ImpersonationBanner re-renders on the contractor side.
  const role = me.data?.role;
  useEffect(() => {
    if (!IS_STAFF_CONSOLE) return;
    if (!role) return;
    if (role === "pencilout_admin") return;
    if (typeof window === "undefined") return;
    const currentOrigin = `${window.location.protocol}//${window.location.host}`;
    if (currentOrigin === CONTRACTOR_APP_ORIGIN) return;
    window.location.assign(`${CONTRACTOR_APP_ORIGIN}/jobs`);
  }, [role]);

  if (IS_STAFF_CONSOLE && role && role !== "pencilout_admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-graphite-soft">
        <span className="font-mono text-sm">Switching to contractor view…</span>
      </div>
    );
  }

  const visibleSections = NAV_SECTIONS.filter((s) => s.targets.includes(targetForFilter));

  return (
    // Outer shell takes EXACTLY the viewport height (not min-h)
    // and clips overflow at the document level. This is what makes
    // the sidebar + footer stay pinned: they're in-flow children
    // of a fixed-height container, so the only thing that can
    // scroll is the <main> below (which gets overflow-y-auto).
    // Without this, the document body grows past the viewport when
    // page content is tall and the whole shell scrolls — sidebar
    // and footer along with it.
    <div className="flex h-screen flex-col overflow-hidden bg-paper text-graphite">
      {me.data?.terms_accept_required && <TermsReacceptModal />}
      <ImpersonationBanner />

      <div className="flex flex-1 min-h-0">
        {/* Sidebar — in-flow on desktop (lg+), drawer on mobile.
            Pinned-feel comes from the outer h-screen + min-h-0
            chain, not position:fixed. */}
        <Sidebar
          sections={visibleSections}
          collapsed={collapsed}
          inboxCount={inboxCount}
          mobileOpen={mobileDrawerOpen}
          onCloseMobile={() => setMobileDrawerOpen(false)}
          onToggleCollapse={() => setCollapsedPersisted(!collapsed)}
          user={me.data ?? null}
          onLogout={() => logout.mutate()}
          loggingOut={logout.isPending}
        />

        {/* Mobile drawer backdrop. Only renders when open; tapping
            closes the drawer. ``lg:hidden`` ensures it never renders
            on desktop where the sidebar is part of the flow. */}
        {mobileDrawerOpen && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileDrawerOpen(false)}
            className="fixed inset-0 z-30 bg-graphite/40 lg:hidden"
          />
        )}

        {/* Right column — slim top bar + main content + footer.
            ``min-h-0`` is required for the inner flex-1 main to
            actually constrain its overflow-y-auto. Without it,
            flex children default to min-content and overflow-auto
            never kicks in — document scrolls instead. */}
        <div className="flex flex-1 min-h-0 min-w-0 flex-col">
          <TopBar onOpenDrawer={() => setMobileDrawerOpen(true)} />

          {/* Main is the ONLY scroll surface. Sidebar/footer stay
              pinned because they're flex-none siblings of this
              flex-1 overflow-y-auto block. */}
          <main className="flex-1 min-h-0 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8">
            <div className="mx-auto w-full max-w-7xl">
              <Outlet />
            </div>
          </main>

          <footer className="flex-none border-t border-rule bg-paper-warm py-4">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 sm:px-6">
              <span className="label-mono">© 2026 PencilOut</span>
              <span className="label-mono space-x-3">
                <NavLink to="/terms" className="hover:text-graphite">
                  Terms
                </NavLink>
                <span aria-hidden>·</span>
                <NavLink to="/privacy" className="hover:text-graphite">
                  Privacy
                </NavLink>
                <span aria-hidden>·</span>
                <a href="mailto:support@pencilout.app" className="hover:text-graphite">
                  Support
                </a>
              </span>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Sidebar — fixed on lg+, drawer on mobile. Renders sections
// followed by a user widget at the bottom that links to /profile
// and houses the sign-out button.
// ---------------------------------------------------------------

interface SidebarProps {
  sections: NavSection[];
  collapsed: boolean;
  inboxCount: number;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onToggleCollapse: () => void;
  user: { display_name: string; role: string; organization_name: string | null } | null;
  onLogout: () => void;
  loggingOut: boolean;
}

function Sidebar({
  sections,
  collapsed,
  inboxCount,
  mobileOpen,
  onCloseMobile,
  onToggleCollapse,
  user,
  onLogout,
  loggingOut,
}: SidebarProps) {
  const widthClass = collapsed ? "lg:w-16" : "lg:w-60";

  return (
    <aside
      className={[
        // Mobile: fixed-position drawer. Slides in from the left;
        // off-screen by default. ``z-40`` puts it above the
        // backdrop button (z-30).
        "fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-rule bg-paper-warm transition-transform",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // Desktop: in-flow column, no transform, width controlled
        // by collapsed state.
        "lg:relative lg:translate-x-0",
        widthClass,
      ].join(" ")}
      aria-label="Primary"
    >
      {/* Brand / collapse-toggle row. Brand wordmark on the left;
          mobile gets a close (X) button, desktop gets a chevron
          collapse toggle. */}
      <div className="flex h-14 flex-none items-center justify-between gap-2 border-b border-rule px-3">
        <NavLink to="/" onClick={onCloseMobile} className="flex items-center gap-2 overflow-hidden">
          <span className={["brand-wordmark text-xl", collapsed ? "lg:hidden" : ""].join(" ")}>
            PencilOut
          </span>
          {collapsed && (
            // Compact PO mark when collapsed — keeps the brand
            // identity without eating row width.
            <span className="hidden brand-wordmark text-xl lg:inline">PO</span>
          )}
          {!collapsed && <span className="label-mono">{IS_STAFF_CONSOLE ? "staff" : "app"}</span>}
        </NavLink>
        <div className="flex items-center gap-1">
          {/* Mobile close button */}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close menu"
            className="rounded p-1 text-graphite-soft hover:bg-paper hover:text-graphite lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
          {/* Desktop collapse toggle */}
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden rounded p-1 text-graphite-soft hover:bg-paper hover:text-graphite lg:block"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Sections. Scrolls if there are too many items for the
          viewport; minimal scrollbar styling. */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section, idx) => (
          <SidebarSection
            key={`${section.title}-${idx}`}
            section={section}
            collapsed={collapsed}
            inboxCount={inboxCount}
            onItemClick={onCloseMobile}
          />
        ))}
      </nav>

      {/* User widget — pinned to bottom. Links to /profile; sign-
          out button next to it. Compact when collapsed. */}
      {user && (
        <div className="flex-none border-t border-rule p-2">
          <NavLink
            to="/profile"
            onClick={onCloseMobile}
            className={({ isActive }) =>
              [
                "flex items-center gap-2 overflow-hidden rounded px-2 py-1.5 text-left transition",
                isActive ? "bg-pencil-yellow-soft" : "hover:bg-pencil-yellow-soft",
              ].join(" ")
            }
            title="Edit profile"
          >
            {/* Avatar circle — initials. Stays visible when collapsed
                so the bottom of the sidebar always has an anchor. */}
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-graphite font-mono text-xs font-bold text-paper">
              {initials(user.display_name)}
            </span>
            <div className={["min-w-0 flex-1", collapsed ? "lg:hidden" : ""].join(" ")}>
              <div className="truncate whitespace-nowrap text-sm font-medium leading-tight">
                {user.display_name}
              </div>
              <div className="truncate whitespace-nowrap font-mono text-xs leading-tight text-graphite-soft">
                {user.role === "pencilout_admin"
                  ? "PencilOut staff"
                  : user.organization_name || "—"}
              </div>
            </div>
          </NavLink>
          <button
            type="button"
            onClick={() => {
              onLogout();
              onCloseMobile();
            }}
            disabled={loggingOut}
            title="Sign out"
            className={[
              "mt-1 flex w-full items-center gap-2 rounded px-2 py-1.5 font-mono text-xs text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50",
              collapsed ? "lg:justify-center" : "",
            ].join(" ")}
          >
            <LogOut className="h-3.5 w-3.5 flex-none" aria-hidden />
            <span className={collapsed ? "lg:hidden" : ""}>
              {loggingOut ? "Signing out…" : "Sign out"}
            </span>
          </button>
        </div>
      )}
    </aside>
  );
}

function SidebarSection({
  section,
  collapsed,
  inboxCount,
  onItemClick,
}: {
  section: NavSection;
  collapsed: boolean;
  inboxCount: number;
  onItemClick: () => void;
}) {
  return (
    <div className="mb-3 last:mb-0">
      {/* Section title — hidden when collapsed (text doesn't fit in
          the icon-only rail). Replaced by a thin divider so sections
          remain visually distinct. */}
      {section.title && (
        <>
          <div
            className={[
              "px-2 pb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-graphite-faint",
              collapsed ? "lg:hidden" : "",
            ].join(" ")}
          >
            {section.title}
          </div>
          <div
            className={["mb-1 hidden h-px w-full bg-rule", collapsed ? "lg:block" : ""].join(" ")}
            aria-hidden
          />
        </>
      )}
      {section.items.map((item) => (
        <SidebarItem
          key={item.to}
          item={item}
          collapsed={collapsed}
          inboxCount={inboxCount}
          onClick={onItemClick}
        />
      ))}
    </div>
  );
}

function SidebarItem({
  item,
  collapsed,
  inboxCount,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  inboxCount: number;
  onClick: () => void;
}) {
  const isInbox = item.to === "/inbox";
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        [
          "group flex items-center gap-2 overflow-hidden rounded px-2 py-1.5 text-sm font-medium transition",
          isActive
            ? "bg-graphite text-paper"
            : "text-graphite-soft hover:bg-pencil-yellow-soft hover:text-graphite",
          collapsed ? "lg:justify-center" : "",
        ].join(" ")
      }
    >
      <item.icon className="h-4 w-4 flex-none" aria-hidden />
      <span className={["truncate whitespace-nowrap", collapsed ? "lg:hidden" : ""].join(" ")}>
        {item.label}
      </span>
      {isInbox && inboxCount > 0 && (
        <span
          className={[
            "ml-auto rounded bg-pencil-yellow px-1.5 py-0.5 font-mono text-[10px] font-bold text-graphite",
            collapsed ? "lg:hidden" : "",
          ].join(" ")}
        >
          {inboxCount}
        </span>
      )}
    </NavLink>
  );
}

// ---------------------------------------------------------------
// TopBar — slim row above main content. Mobile only contains the
// hamburger button; desktop is mostly empty (sidebar carries the
// brand + nav). Reserved for future per-page actions (search,
// breadcrumbs, etc.) without re-shuffling the shell.
// ---------------------------------------------------------------

function TopBar({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  return (
    <header className="flex h-14 flex-none items-center gap-3 border-b border-rule bg-paper px-4 lg:hidden">
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label="Open menu"
        className="rounded p-1 text-graphite-soft hover:bg-paper-warm hover:text-graphite"
      >
        <Menu className="h-5 w-5" />
      </button>
      <NavLink to="/" className="flex items-center gap-2">
        <span className="brand-wordmark text-xl">PencilOut</span>
        <span className="label-mono">{IS_STAFF_CONSOLE ? "staff" : "app"}</span>
      </NavLink>
    </header>
  );
}

// ---------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const p = parts[0] ?? "";
    return p.slice(0, 2).toUpperCase();
  }
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
